import { Receipt, ReceiptStatusEnum } from '@pxtra-client-lib-legacy/gen';
import {
  ViewModelBasedOn,
  ValueCache,
  MoneyViewModel,
  DateViewModel
} from '../lib/base/viewModels';
import { TFunction } from 'i18next';
import { NavigateFunction } from 'react-router';
import ReceiptStore from '../stores/receipts/details';
import { ReceiptAction } from '../pages/receipts/dialogs/action/viewModel';

export enum ReceiptBenefitType {
  betreuungskosten = 'betreuungskosten',
  essenszuschuss = 'essenszuschuss',
  internetpauschale = 'internetpauschale',
  öpnv_tickets = 'öpnv_tickets',
  telefonkosten = 'telefonkosten'
}

export enum ReceiptStatus {
  Drafted = 'DRAFTED',
  Submitted = 'SUBMITTED',
  Rejected = 'REJECTED',
  Approved = 'APPROVED',
  Billed = 'BILLED',
  Archived = 'ARCHIVED',
  DeletionSubmitted = 'DELETION_SUBMITTED'
}

const ReceiptStatusEnumRecord: Record<ReceiptStatusEnum, ReceiptStatus> = {
  DRAFTED: ReceiptStatus.Drafted,
  SUBMITTED: ReceiptStatus.Submitted,
  REJECTED: ReceiptStatus.Rejected,
  APPROVED: ReceiptStatus.Approved,
  BILLED: ReceiptStatus.Billed,
  ARCHIVED: ReceiptStatus.Archived,
  DELETION_SUBMITTED: ReceiptStatus.DeletionSubmitted
};

export class ReceiptsStatusViewMode {
  constructor(
    public value: ReceiptStatus,
    private translate: TFunction
  ) {}
  get name() {
    return this.translate(`employee:${this.value}`);
  }
}

export class ReceiptsViewModel extends ViewModelBasedOn<Receipt> {
  private cache = new ValueCache();

  constructor(
    model: Receipt,
    private receiptStore: ReceiptStore,
    private t: TFunction,
    private navigate: NavigateFunction
  ) {
    super(model);
  }

  get id() {
    return this.model.id;
  }

  get benefit() {
    return this.model.benefit;
  }

  get employeeName() {
    return this.model.employee_name;
  }

  get date() {
    return DateViewModel.fromDjango(this.model.date ?? '');
  }

  get updatedAt() {
    const value = this.model.updated_at.split('T')[0];
    return DateViewModel.fromDjango(value);
  }

  get amount() {
    return new MoneyViewModel(this.model.amount ?? 0);
  }

  get note() {
    return this.model.note;
  }

  get status() {
    return new ReceiptsStatusViewMode(
      ReceiptStatusEnumRecord[this.model.status ?? ReceiptStatus.Drafted],
      this.t
    );
  }

  get isApplicable() {
    return (action: ReceiptAction): boolean => {
      const actions = {
        delete:
          this.status.value === ReceiptStatus.Drafted ||
          this.status.value === ReceiptStatus.Submitted,
        approve:
          this.status.value === ReceiptStatus.Submitted ||
          this.status.value === ReceiptStatus.Rejected,
        reject:
          this.status.value === ReceiptStatus.Submitted ||
          this.status.value === ReceiptStatus.Approved,
        settle: this.status.value === ReceiptStatus.Approved,
        edit:
          this.status.value !== ReceiptStatus.Billed &&
          this.status.value !== ReceiptStatus.DeletionSubmitted &&
          this.status.value !== ReceiptStatus.Archived
      };

      return actions[action];
    };
  }

  get detailsPagePath() {
    if (location.pathname.includes('employee')) {
      return `/employees/receipts/${this.id}`;
    }
    return `/receipts/${this.id}`;
  }

  openDetails() {
    this.navigate(this.detailsPagePath);
  }

  openEdit() {
    this.navigate(`${this.detailsPagePath}/edit`);
  }

  delete() {
    this.receiptStore.delete(this.id);
  }
  approve() {
    this.receiptStore.approve(this.id);
  }
  reject(note?: string) {
    this.receiptStore.reject(this.id, note);
  }
  settle() {
    this.receiptStore.settle(this.id);
  }
}
